(function () {
  'use strict';

  var ROOT_CLASS = 'pdp-studio-alpha-v16';
  var IMAGE_SELECTOR = [
    '.shopify-section--main-product .product-gallery__media img',
    '.shopify-section--main-product variant-picker .media-swatch > img'
  ].join(', ');
  var cache = new Map();
  var cacheOrder = [];
  var objectUrls = [];
  var scanQueued = false;
  var lastVariant = '';
  var alignTimer = 0;
  var galleryPointerActive = false;

  if (!document.documentElement.classList.contains(ROOT_CLASS)) {
    return;
  }

  function reveal(image, state) {
    image.classList.remove('pdp-studio-alpha-pending');
    image.classList.add('pdp-studio-alpha-ready');
    image.dataset.pdpStudioAlphaState = state;
  }

  function cacheObjectUrl(source, objectUrl) {
    cache.set(source, objectUrl);
    cacheOrder.push(source);
    objectUrls.push(objectUrl);

    while (cacheOrder.length > 18) {
      var staleSource = cacheOrder.shift();
      cache.delete(staleSource);
    }
  }

  function isBrightNeutral(data, offset, minimum, maximumSpread) {
    var red = data[offset];
    var green = data[offset + 1];
    var blue = data[offset + 2];
    var low = Math.min(red, green, blue);
    var high = Math.max(red, green, blue);
    return low >= minimum && high - low <= maximumSpread;
  }

  function sideWhiteRatios(data, width, height) {
    var band = Math.max(2, Math.round(Math.min(width, height) * 0.008));
    var counts = [0, 0, 0, 0];
    var totals = [0, 0, 0, 0];
    var x;
    var y;
    var offset;

    for (x = 0; x < width; x += 1) {
      for (y = 0; y < band; y += 1) {
        offset = (y * width + x) * 4;
        totals[0] += 1;
        if (isBrightNeutral(data, offset, 247, 10)) counts[0] += 1;

        offset = ((height - 1 - y) * width + x) * 4;
        totals[1] += 1;
        if (isBrightNeutral(data, offset, 247, 10)) counts[1] += 1;
      }
    }

    for (y = band; y < height - band; y += 1) {
      for (x = 0; x < band; x += 1) {
        offset = (y * width + x) * 4;
        totals[2] += 1;
        if (isBrightNeutral(data, offset, 247, 10)) counts[2] += 1;

        offset = (y * width + width - 1 - x) * 4;
        totals[3] += 1;
        if (isBrightNeutral(data, offset, 247, 10)) counts[3] += 1;
      }
    }

    return counts.map(function (count, index) {
      return totals[index] ? count / totals[index] : 0;
    });
  }

  function removeConnectedWhite(data, width, height) {
    var pixelCount = width * height;
    var transparent = new Uint8Array(pixelCount);
    var queue = new Uint32Array(pixelCount);
    var head = 0;
    var tail = 0;

    function add(pixel) {
      if (transparent[pixel]) return;
      var offset = pixel * 4;
      if (!isBrightNeutral(data, offset, 247, 10)) return;
      transparent[pixel] = 1;
      queue[tail++] = pixel;
    }

    var x;
    var y;
    for (x = 0; x < width; x += 1) {
      add(x);
      add((height - 1) * width + x);
    }
    for (y = 1; y < height - 1; y += 1) {
      add(y * width);
      add(y * width + width - 1);
    }

    while (head < tail) {
      var pixel = queue[head++];
      var pixelX = pixel % width;
      if (pixelX > 0) add(pixel - 1);
      if (pixelX < width - 1) add(pixel + 1);
      if (pixel >= width) add(pixel - width);
      if (pixel < pixelCount - width) add(pixel + width);
    }

    if (tail < pixelCount * 0.18) {
      return false;
    }

    for (var index = 0; index < pixelCount; index += 1) {
      if (transparent[index]) data[index * 4 + 3] = 0;
    }

    /* Keep the product silhouette intact while softening only the one-pixel
       compression fringe that directly touches the removed studio field. */
    var featherAlpha = new Uint8Array(pixelCount);
    for (y = 1; y < height - 1; y += 1) {
      for (x = 1; x < width - 1; x += 1) {
        var current = y * width + x;
        if (transparent[current]) continue;
        var currentOffset = current * 4;
        if (!isBrightNeutral(data, currentOffset, 232, 18)) continue;
        if (
          transparent[current - 1] ||
          transparent[current + 1] ||
          transparent[current - width] ||
          transparent[current + width]
        ) {
          var average = (data[currentOffset] + data[currentOffset + 1] + data[currentOffset + 2]) / 3;
          featherAlpha[current] = Math.round(255 * Math.min(1, Math.max(0, (255 - average) / 18)));
        }
      }
    }
    for (var featherIndex = 0; featherIndex < pixelCount; featherIndex += 1) {
      if (featherAlpha[featherIndex]) data[featherIndex * 4 + 3] = featherAlpha[featherIndex];
    }

    return true;
  }

  function applyObjectUrl(image, source, objectUrl) {
    image.dataset.pdpStudioAlphaOriginal = source;
    image.dataset.pdpStudioAlphaObject = objectUrl;
    image.classList.add('pdp-transparent-product-image');
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = objectUrl;
    reveal(image, 'transparent');
  }

  function processLoadedImage(image, source) {
    if (!image.isConnected || image.dataset.pdpStudioAlphaSource !== source) return;

    var cachedUrl = cache.get(source);
    if (cachedUrl) {
      applyObjectUrl(image, source, cachedUrl);
      return;
    }

    var canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    var context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      reveal(image, 'canvas-unavailable');
      return;
    }

    try {
      context.drawImage(image, 0, 0);
      var imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      var ratios = sideWhiteRatios(imageData.data, canvas.width, canvas.height);

      /* A real studio cutout must have the same bright neutral field on all
         four sides. Lifestyle photography is revealed untouched. */
      if (ratios.some(function (ratio) { return ratio < 0.88; })) {
        reveal(image, 'photograph');
        return;
      }

      if (!removeConnectedWhite(imageData.data, canvas.width, canvas.height)) {
        reveal(image, 'not-enough-background');
        return;
      }

      context.putImageData(imageData, 0, 0);
      canvas.toBlob(function (blob) {
        if (!blob || !image.isConnected || image.dataset.pdpStudioAlphaSource !== source) {
          reveal(image, 'encode-fallback');
          return;
        }
        var objectUrl = URL.createObjectURL(blob);
        cacheObjectUrl(source, objectUrl);
        applyObjectUrl(image, source, objectUrl);
      }, 'image/png');
    } catch (error) {
      reveal(image, 'security-fallback');
    }
  }

  function processImage(image) {
    if (!(image instanceof HTMLImageElement)) return;
    if (image.classList.contains('pdp-transparent-product-image')) {
      if (image.dataset.pdpStudioAlphaOriginal) {
        image.classList.add('pdp-studio-alpha-ready');
      } else {
        reveal(image, 'theme-alpha');
      }
      return;
    }

    var source = image.currentSrc || image.src;
    if (!source || source.indexOf('blob:') === 0) return;
    if (image.dataset.pdpStudioAlphaSource === source) return;

    image.dataset.pdpStudioAlphaSource = source;
    image.classList.remove('pdp-studio-alpha-ready');
    image.classList.add('pdp-studio-alpha-pending');

    if (image.complete && image.naturalWidth > 1) {
      processLoadedImage(image, source);
    } else {
      image.addEventListener('load', function onStudioImageLoad() {
        image.removeEventListener('load', onStudioImageLoad);
        var loadedSource = image.currentSrc || image.src;
        image.dataset.pdpStudioAlphaSource = loadedSource;
        processLoadedImage(image, loadedSource);
      }, { once: true });
      image.addEventListener('error', function () {
        reveal(image, 'load-error');
      }, { once: true });
    }
  }

  function alignSelectedCarousel() {
    if (galleryPointerActive) return;
    var carousel = document.querySelector('media-carousel.product-gallery__media-list');
    if (!carousel) return;
    var selected = carousel.querySelector('.product-gallery__media.is-selected') || carousel.querySelector('.product-gallery__media');
    if (!selected) return;
    var target = selected.offsetLeft;
    if (Math.abs(carousel.scrollLeft - target) > 2) carousel.scrollLeft = target;
  }

  function scheduleSelectedAlignment() {
    window.clearTimeout(alignTimer);
    alignTimer = window.setTimeout(function () {
      requestAnimationFrame(function () {
        alignSelectedCarousel();
        window.setTimeout(alignSelectedCarousel, 180);
      });
    }, 40);
  }

  function scan() {
    scanQueued = false;
    var media = Array.prototype.slice.call(document.querySelectorAll('.shopify-section--main-product .product-gallery__media'));
    var selectedMedia = document.querySelector('.shopify-section--main-product .product-gallery__media.is-selected') || media[0];
    var selectedIndex = media.indexOf(selectedMedia);
    var priorityImages = Array.prototype.slice.call(
      document.querySelectorAll('.shopify-section--main-product variant-picker .media-swatch > img')
    );

    /* Large configurable products can contain hundreds of media nodes. Process
       only the selected slide and its immediate neighbors; newly selected
       slides are picked up by the observer before they are revealed. */
    [-1, 0, 1].forEach(function (distance) {
      var item = media[selectedIndex + distance];
      var image = item && item.querySelector('img');
      if (image) priorityImages.push(image);
    });
    priorityImages.forEach(processImage);

    var currentVariant = new URLSearchParams(window.location.search).get('variant') || '';
    if (currentVariant !== lastVariant) {
      lastVariant = currentVariant;
      scheduleSelectedAlignment();
    }
  }

  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(scan);
  }

  var observer = new MutationObserver(function (mutations) {
    var shouldScan = false;
    mutations.forEach(function (mutation) {
      if (mutation.type === 'childList') {
        shouldScan = true;
        return;
      }
      if (mutation.type !== 'attributes') return;
      if (mutation.attributeName === 'class') {
        if (mutation.target.classList && mutation.target.classList.contains('product-gallery__media')) {
          shouldScan = true;
          scheduleSelectedAlignment();
        }
        return;
      }
      if (!(mutation.target instanceof HTMLImageElement)) return;
      var image = mutation.target;
      var objectUrl = image.dataset.pdpStudioAlphaObject;
      var current = image.currentSrc || image.src;
      if (objectUrl && current === objectUrl) return;
      if (image.matches(IMAGE_SELECTOR) && image.dataset.pdpStudioAlphaOriginal !== current) {
        image.classList.remove('pdp-transparent-product-image', 'pdp-studio-alpha-ready');
        delete image.dataset.pdpStudioAlphaSource;
        delete image.dataset.pdpStudioAlphaObject;
        delete image.dataset.pdpStudioAlphaOriginal;
      }
      shouldScan = true;
    });
    if (shouldScan) queueScan();
  });

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['src', 'srcset', 'class']
  });

  document.addEventListener('shopify:section:load', queueScan);
  document.addEventListener('variant:change', function () {
    queueScan();
    scheduleSelectedAlignment();
  });
  document.addEventListener('pointerdown', function (event) {
    if (event.target.closest && event.target.closest('media-carousel.product-gallery__media-list')) {
      galleryPointerActive = true;
    }
  }, true);
  document.addEventListener('pointerup', function () {
    if (!galleryPointerActive) return;
    galleryPointerActive = false;
    scheduleSelectedAlignment();
  }, true);
  document.addEventListener('pointercancel', function () {
    galleryPointerActive = false;
  }, true);
  queueScan();

  window.setTimeout(function () {
    document.querySelectorAll(IMAGE_SELECTOR + ':not(.pdp-studio-alpha-ready)').forEach(function (image) {
      if (
        image.complete &&
        image.naturalWidth > 1 &&
        image.closest('.product-gallery__media.is-selected, variant-picker .media-swatch')
      ) reveal(image, 'timeout-fallback');
    });
  }, 3500);

  window.addEventListener('pagehide', function () {
    objectUrls.forEach(function (objectUrl) { URL.revokeObjectURL(objectUrl); });
    objectUrls.length = 0;
    cache.clear();
  }, { once: true });
})();
